/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import { Box, Button, Chip, Grid, MenuItem, Popover } from '@mui/material';
import { format, parseISO } from 'date-fns';
import {
  compact,
  debounce,
  difference,
  find,
  flatMap,
  includes,
  isEmpty,
  isNil,
  map,
} from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';
import DateField from 'common/forms/DateField';
import {
  GRAPHQL_INT_MAX,
  validateMaxNumber,
  validateMinLength,
  validatePositiveInteger,
} from 'common/forms/formValidations';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import { buildCatalogProductOptionRenderer } from 'common/forms/CatalogProductOptionRenderer';
import lineItemEnum from 'enums/lineItem.enum';
import { listCatalogProductsByDrugName, listDrugProfilesByDrugName } from 'store/thunks/drugThunks';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { filterBulkLabels } from 'store/thunks/workQueueThunks';

import { NDC_FILTER_TOOLTIP } from 'modules/WorkQueue/constants/filterTooltips';
import { tagMatchModeForFilter, tagMatchModeFormValue } from 'modules/WorkQueue/utils/tagMatchMode';

import SYSTEM_TAG_NAMES from 'common/constants/systemTags';
import { EnableInventoryLookup, casingFormat } from 'common/utils';
import { ItemType, OrderFillType, SortKey } from 'enums/common.enum';

import FacilityFilterField from './FacilityFilterField';
import TagMatchModeField from './TagMatchModeField';

const BulkLabelsFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [practices, setPractices] = useState([]);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [drugLoading, setDrugLoading] = useState(false);
  const [drugProfileLoading, setDrugProfileLoading] = useState(false);
  const popoverId = anchorEl ? 'WorkQueue-BulkLabelsFilterButton-Popover' : undefined;
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const loading = useSelector((state) => state.workQueue.bulkLabels.loading);
  const tagOptions = useSelector(({ systemConfigs }) => systemConfigs.lineItemTags || []);
  const selectedDrugProfilesData = useSelector(
    ({ workQueue }) => workQueue.bulkLabels.filter?.selectedDrugProfiles || []
  );
  // POM-5850: catalog-backed Drug Name selections, live only when EnableInventoryLookup is on.
  const selectedDrugProductsData = useSelector(
    ({ workQueue }) => workQueue.bulkLabels.filter?.selectedDrugProducts || []
  );
  const [drugNameOptions, setDrugNameOptions] = useState([]);
  const [drugProfileOptions, setDrugProfileOptions] = useState([]);

  const formatTag = (tag) => casingFormat(tag?.replace(/_/g, ' '));

  const isSystemTagOption = (originalTagName) => SYSTEM_TAG_NAMES.includes(originalTagName);

  const sortedTagOptions = useMemo(() => {
    const visibleTags = tagOptions.filter((t) => t !== 'BACKFILLED_SHIP_BY');
    const userTags = visibleTags
      .filter((t) => !isSystemTagOption(t))
      .sort((a, b) => a.localeCompare(b));
    const systemTags = visibleTags
      .filter((t) => isSystemTagOption(t))
      .sort((a, b) => a.localeCompare(b));
    return [...userTags, ...systemTags];
  }, [tagOptions]);

  const renderTagOption = (props, option) => {
    // eslint-disable-next-line react/prop-types
    const { key, ...optionProps } = props;
    const originalTag = tagOptions.find((t) => formatTag(t) === option);
    const isSystem = originalTag ? isSystemTagOption(originalTag) : false;
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <li key={key} {...optionProps}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <span>{option}</span>
          {isSystem && (
            <Chip
              label='System'
              size='small'
              sx={{ ml: 1, height: 18, fontSize: '0.65rem', opacity: 0.7 }}
            />
          )}
        </Box>
      </li>
    );
  };

  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedPracticeIds, setSelectedPracticeIds] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [disablePartnerFilter, setDisablePartnerFilter] = useState(false);
  const [disableNotInPartnerFilter, setDisableNotInPartnerFilter] = useState(false);
  const [selectedNotInPartners, setSelectedNotInPartners] = useState([]);
  const [notSelectedPartnerId, setNotSelectedPartnerId] = useState([]);

  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTagsNotIn, setSelectedTagsNotIn] = useState([]);
  const [selectedNdcs, setSelectedNdcs] = useState([]);
  const [selectedDrugProfileObjects, setSelectedDrugProfileObjects] = useState([]);
  const [selectedDrugProfiles, setSelectedDrugProfiles] = useState([]);
  // POM-5850: each entry is { label, productIds } — catalog.product_id has no unique constraint,
  // so one "name strength" label can cover several products and all of them must be filtered on.
  const [selectedDrugProductObjects, setSelectedDrugProductObjects] = useState([]);
  const [selectedDrugProducts, setSelectedDrugProducts] = useState([]);
  const [drugNameInputValue, setDrugNameInputValue] = useState('');
  const [drugProfileInputValue, setDrugProfileInputValue] = useState('');

  const filter = useSelector(({ workQueue: { bulkLabels } }) => bulkLabels.filter);

  const utcStringToLocalDate = (isoString) => {
    if (!isoString) return undefined;
    return new Date(isoString);
  };

  const formInitialValues = useMemo(
    () => ({
      ...filter,
      lastPrintedDate: utcStringToLocalDate(filter?.lastPrintedDate),
      shipByDate: filter?.shipByDate && parseISO(filter?.shipByDate),
      orderDate: utcStringToLocalDate(filter?.orderDate),
      labelDate: utcStringToLocalDate(filter?.labelDate),
      tagMatchMode: tagMatchModeFormValue(filter),
    }),
    [filter]
  );

  const partner = useMemo(() => {
    return map(partners, ({ partnerName }) => casingFormat(partnerName));
  }, [partners]);

  const selectedPracticeNames = useMemo(() => {
    return selectedPracticeIds.map((practiceId) =>
      casingFormat(find(practices, { practiceId })?.practiceName)
    );
  }, [selectedPracticeIds, practices]);

  // POM-5850: with the inventory lookup on, Drug Name options come from the catalog (the product
  // master) instead of drug_profiles, and a selection resolves to productIds rather than a name
  // string. listCatalogProductsByDrugName is already scoped to the roles that work this queue.
  const debouncedSearchDrugName = useMemo(() => {
    const searchFunc = (drugName) => {
      setDrugLoading(true);
      const search = EnableInventoryLookup
        ? listCatalogProductsByDrugName(drugName)
        : listDrugProfilesByDrugName(drugName, { excludeDrugStatus: true });

      dispatch(search)
        .then((result) => {
          if (result) {
            setDrugNameOptions(result);
          }
        })
        .finally(() => setDrugLoading(false));
    };

    return debounce(searchFunc, 500, { trailing: true, leading: false });
  }, [dispatch]);

  // Collapse the catalog rows into one option per display name: the same product can appear
  // several times (different pack sizes / dosage forms share a product_id, and a product_id is
  // not unique in the catalog), which would otherwise render as duplicate identical options.
  // POM-5983: group and render on the DB display_name (full name incl. pack size), so pack-size
  // variants stay distinct.
  const drugProductOptionGroups = useMemo(() => {
    if (!EnableInventoryLookup) return [];

    const groupsByLabel = drugNameOptions.reduce((acc, product) => {
      if (!product?.productId) return acc;

      const label = product.displayName;
      const existing = acc.get(label);

      if (existing) {
        if (!existing.productIds.includes(product.productId)) {
          existing.productIds.push(product.productId);
        }
        // POM-6309: a group spans several catalog rows, so it only reads as disabled when every
        // row behind it is — one enabled row still makes the drug orderable.
        existing.isCatalogEnabled = existing.isCatalogEnabled || product.isCatalogEnabled !== false;
        return acc;
      }

      acc.set(label, {
        label,
        productIds: [product.productId],
        isCatalogEnabled: product.isCatalogEnabled !== false,
      });
      return acc;
    }, new Map());

    return [...groupsByLabel.values()];
  }, [drugNameOptions]);

  const debouncedSearchDrugProfile = useMemo(() => {
    const searchFunc = (drugName) => {
      setDrugProfileLoading(true);
      dispatch(
        listDrugProfilesByDrugName(drugName, {
          distinctOnPrescriptionDescription: false,
          excludeDrugStatus: true,
        })
      )
        .then((result) => {
          if (result) {
            setDrugProfileOptions(result);
          }
        })
        .finally(() => setDrugProfileLoading(false));
    };

    return debounce(searchFunc, 500, { trailing: true, leading: false });
  }, [dispatch]);

  useEffect(() => {
    if (drugNameInputValue.length >= 3) {
      debouncedSearchDrugName(drugNameInputValue);
    }
  }, [drugNameInputValue, debouncedSearchDrugName]);

  useEffect(() => {
    if (drugProfileInputValue.length >= 3) {
      debouncedSearchDrugProfile(drugProfileInputValue);
    }
  }, [drugProfileInputValue, debouncedSearchDrugProfile]);

  useEffect(() => {
    if (isEmpty(selectedPartnerIds)) {
      setPractices([]);
      setSelectedPractices([]);
      setSelectedPracticeIds([]);
      return;
    }
    setPracticeLoading(true);
    dispatch(getPartnerPractices({ partnerIds: selectedPartnerIds }))
      .then((practice) => {
        if (practice) {
          setPractices(practice);
        }
      })
      .finally(() => setPracticeLoading(false));
  }, [selectedPartnerIds, dispatch]);

  useEffect(() => {
    if (isEmpty(filter.partnerIds)) {
      setSelectedPartners([]);
      setSelectedPartnerIds([]);
    }
  }, [filter.partnerIds]);

  useEffect(() => {
    if (isEmpty(filter.notInPartnerIds)) {
      setSelectedPartners([]);
      setSelectedPartnerIds([]);
      setNotSelectedPartnerId([]);
      setSelectedNotInPartners([]);
    }
  }, [filter.notInPartnerIds]);

  useEffect(() => {
    if (isEmpty(filter.practiceIds)) {
      setSelectedPractices([]);
      setSelectedPracticeIds([]);
    }
  }, [filter.practiceIds]);

  useEffect(() => {
    if (isEmpty(filter.tags)) {
      setSelectedTags([]);
    }
  }, [filter.tags]);

  useEffect(() => {
    if (isEmpty(filter.tagsNotIn)) {
      setSelectedTagsNotIn([]);
    }
  }, [filter.tagsNotIn]);

  useEffect(() => {
    if (isEmpty(filter.ndcs)) {
      setSelectedNdcs([]);
    }
  }, [filter.ndcs]);

  useEffect(() => {
    if (isEmpty(filter.selectedDrugProfiles)) {
      setSelectedDrugProfileObjects([]);
      setSelectedDrugProfiles([]);
    }
  }, [filter.selectedDrugProfiles]);

  useEffect(() => {
    const notInPartnersEmpty = isEmpty(selectedNotInPartners);
    const partnersEmpty = isEmpty(selectedPartners);

    setDisableNotInPartnerFilter(!partnersEmpty);
    setDisablePartnerFilter(!notInPartnersEmpty);
  }, [selectedNotInPartners, selectedPartners]);

  useEffect(() => {
    if (isEmpty(selectedNotInPartners)) {
      setPractices([]);
      setSelectedPractices([]);
      setSelectedPracticeIds([]);
      setSelectedPartnerIds([]);
    }
  }, [selectedNotInPartners]);

  const getDrugNameNoOptionsText = () => {
    if (drugLoading) return 'Loading...';
    if (drugNameInputValue.length < 3) return 'Type at least 3 characters to search...';
    return 'No results found';
  };

  const getDrugProfileNoOptionsText = () => {
    if (drugProfileLoading) return 'Loading...';
    if (drugProfileInputValue.length < 3) return 'Type at least 3 characters to search...';
    return 'No results found';
  };

  const handleOpen = (event) => {
    // The Drug Name field is backed by selectedDrugProducts once the lookup is on, and by the
    // medicationNames string list before that — keep the reset check on whichever is live.
    const hasDrugNameSelection = EnableInventoryLookup
      ? !isEmpty(filter?.selectedDrugProducts)
      : !isEmpty(filter?.medicationNames);

    if (!hasDrugNameSelection) {
      setDrugNameOptions([]);
      setDrugNameInputValue('');
    }

    if (isEmpty(filter?.selectedDrugProfiles)) {
      setDrugProfileOptions([]);
      setDrugProfileInputValue('');
    }

    if (filter?.notInPartnerIds) {
      setNotSelectedPartnerId(filter?.notInPartnerIds);
      setSelectedNotInPartners(
        filter.notInPartnerIds.map(
          (partnerId) => find(partners, { partnerUuid: partnerId })?.partnerName
        )
      );

      const filteredPartners = partners
        .filter((value) => !includes(filter?.notInPartnerIds, value.partnerUuid))
        .map((value) => value.partnerUuid);

      setSelectedPartnerIds(filteredPartners);
      setSelectedPartners([]);
    } else {
      setNotSelectedPartnerId([]);
      setSelectedNotInPartners((prev) => (isEmpty(prev) ? prev : []));

      if (filter?.partnerIds) {
        setSelectedPartnerIds(filter.partnerIds);
        setSelectedPartners(
          filter.partnerIds.map((partnerId) =>
            casingFormat(find(partners, { partnerUuid: partnerId })?.partnerName)
          )
        );
      } else {
        setSelectedPartnerIds([]);
        setSelectedPartners([]);
      }
    }

    if (filter?.practiceIds) {
      setSelectedPracticeIds(filter.practiceIds);
    } else {
      setSelectedPractices([]);
      setSelectedPracticeIds([]);
    }

    if (filter?.tags) {
      setSelectedTags(filter.tags);
    } else {
      setSelectedTags([]);
    }

    if (filter?.tagsNotIn) {
      setSelectedTagsNotIn(filter.tagsNotIn);
    } else {
      setSelectedTagsNotIn([]);
    }

    if (filter?.ndcs) {
      setSelectedNdcs(filter.ndcs);
    } else {
      setSelectedNdcs([]);
    }

    if (!isEmpty(selectedDrugProfilesData)) {
      setSelectedDrugProfileObjects(selectedDrugProfilesData);
      const displayValues = selectedDrugProfilesData.map((drug) => {
        const displayText =
          drug.strength && drug.form
            ? `${drug.drugName} - ${drug.strength} ${drug.form}`
            : drug.drugName;
        return displayText;
      });
      setSelectedDrugProfiles(displayValues);
    } else {
      setSelectedDrugProfileObjects([]);
      setSelectedDrugProfiles([]);
    }

    if (!isEmpty(selectedDrugProductsData)) {
      setSelectedDrugProductObjects(selectedDrugProductsData);
      setSelectedDrugProducts(selectedDrugProductsData.map((product) => product.label));
    } else {
      setSelectedDrugProductObjects([]);
      setSelectedDrugProducts([]);
    }

    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const dateToUTCString = (date) => {
    if (!date) return undefined;
    return date.toISOString();
  };

  const dateToDateString = (date) => {
    if (!date) return undefined;
    return format(date, 'yyyy-MM-dd');
  };

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    // `drugProducts` / `drugProfiles` hold display labels for the autocomplete only — the
    // identities they resolve to travel in selectedDrugProducts / selectedDrugProfiles.
    const { drugProfiles, drugProducts, ...restFormData } = formData;

    const selectedDrugProfileData = !isEmpty(selectedDrugProfileObjects)
      ? selectedDrugProfileObjects.map((drug) => ({
          drugProfileId: drug.drugProfileId,
          drugName: drug.drugName,
          strength: drug.strength,
          form: drug.form,
        }))
      : [];

    const selectedDrugProductData = !isEmpty(selectedDrugProductObjects)
      ? selectedDrugProductObjects.map((product) => ({
          label: product.label,
          productIds: product.productIds,
        }))
      : [];

    dispatch(
      filterBulkLabels({
        filter: {
          ...restFormData,
          states: !isEmpty(formData.states) ? formData.states : undefined,
          ndcs: EnableInventoryLookup && !isEmpty(selectedNdcs) ? selectedNdcs : undefined,
          // With the lookup on, Drug Name filters by product identity, so the free-text name
          // list must not also be sent — core ANDs its clauses and the two would conflict.
          medicationNames:
            !EnableInventoryLookup && !isEmpty(formData.medicationNames)
              ? formData.medicationNames
              : undefined,
          selectedDrugProfiles: selectedDrugProfileData,
          selectedDrugProducts: selectedDrugProductData,
          tags: !isEmpty(selectedTags) ? selectedTags : undefined,
          tagMatchMode: tagMatchModeForFilter(formData.tagMatchMode, selectedTags),
          tagsNotIn: !isEmpty(selectedTagsNotIn) ? selectedTagsNotIn : undefined,
          partnerIds: !isEmpty(formData.partnerIds) ? selectedPartnerIds : undefined,
          practiceIds: !isEmpty(selectedPracticeIds) ? selectedPracticeIds : undefined,
          lastPrintedDate: dateToUTCString(formData.lastPrintedDate),
          shipByDate: dateToDateString(formData.shipByDate),
          notInPartnerIds: !isEmpty(formData.notInPartnerIds) ? notSelectedPartnerId : undefined,
          ...(!isNil(formData.isReshipment) && { isReshipment: formData.isReshipment }),
          ...(formData.batchStatus && { batchStatus: formData.batchStatus }),
          ...(formData.orderDate && {
            orderDate: dateToUTCString(formData.orderDate),
          }),
          ...(formData.labelDate && {
            labelDate: dateToUTCString(formData.labelDate),
          }),
          ...(formData.itemCount && {
            itemCount: Number(formData.itemCount),
          }),
          ...(formData.quantity && {
            quantity: Number(formData.quantity),
          }),
        },
      })
    );
  };

  const getPartnerUUIDS = (newValue) => {
    const partnerIds = newValue.map((partnerName) => {
      const foundPartner = partners.find(
        (partnerData) => partnerData.partnerName.toLowerCase() === partnerName.toLowerCase()
      );
      return foundPartner?.partnerUuid;
    });

    return partnerIds;
  };

  const getRemovedPartnerIds = (collection, comparator) => {
    const removedPartnerIds = difference(collection, comparator);

    return removedPartnerIds;
  };

  const handlePartnerChange = (newValue) => {
    const partnerId = getPartnerUUIDS(newValue);
    const removedPartnerIds = getRemovedPartnerIds(selectedPartnerIds, partnerId);

    setSelectedPartners(newValue);
    setSelectedPartnerIds(partnerId);

    if (!isEmpty(removedPartnerIds)) {
      const practicesToRemove = practices.filter(
        (practice) =>
          removedPartnerIds.includes(practice.partnerId) &&
          selectedPracticeIds.includes(practice.practiceId)
      );

      const practiceNamesToRemove = practicesToRemove.map((practice) => practice.practiceName);
      const practiceIdsToRemove = practicesToRemove.map((practice) => practice.practiceId);

      setSelectedPractices((existingSelectedPartners) =>
        difference(existingSelectedPartners, practiceNamesToRemove)
      );
      setSelectedPracticeIds((existingSelectedPractice) =>
        difference(existingSelectedPractice, practiceIdsToRemove)
      );
    }
  };

  const handleNotInPartnerChange = (newValue) => {
    const partnerUUIDS = getPartnerUUIDS(newValue);
    setSelectedNotInPartners(newValue);

    const filteredPartners = partners
      .filter((value) => !includes(partnerUUIDS, value.partnerUuid))
      .map((value) => value.partnerUuid);

    setSelectedPartnerIds(filteredPartners);
    setNotSelectedPartnerId(partnerUUIDS);

    const partnerPractices = compact(flatMap(map(partners, 'partnerPractices')));

    const allowedPracticeName = partnerPractices
      .filter((pp) => includes(filteredPartners, pp.partnerId))
      .map((pp) => pp.practiceName);

    const allowedPracticeIds = partnerPractices
      .filter((pp) => includes(filteredPartners, pp.partnerId))
      .map((pp) => pp.practiceId);

    const removeDeselectedPractice = selectedPracticeNames
      .filter((value) => includes(allowedPracticeName, value))
      .map((value) => value);

    const removeDeselectedPracticeIds = selectedPracticeIds
      .filter((value) => includes(allowedPracticeIds, value))
      .map((value) => value);

    setSelectedPractices(removeDeselectedPractice);
    setSelectedPracticeIds(removeDeselectedPracticeIds);
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '70vh',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={formInitialValues}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid, values }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Item Type'
                      name={SortKey.ITEM_TYPE}
                      id='WorkQueue-BulkLabelsFilterButton-itemType-dropdown'
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value={ItemType.RX}>RX</MenuItem>
                      <MenuItem value={ItemType.OTC}>OTC</MenuItem>
                    </SelectField>
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Patient'
                      name='patientName'
                      id='WorkQueue-BulkLabelsFilterButton-patientName-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Order Id'
                      name='orderId'
                      id='WorkQueue-BulkLabelsFilterButton-orderId-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  {EnableInventoryLookup && (
                    <Grid item sx={{ py: 0.5 }}>
                      <AutoCompleteMultipleSelect
                        label='NDC'
                        name='ndcs'
                        id='WorkQueue-BulkLabelsFilterButton-ndcs-input'
                        multiple
                        freeSolo
                        options={[]}
                        formatOption={(value) => value}
                        defaultValue={selectedNdcs}
                        value={selectedNdcs}
                        onChange={(event, newValue) => setSelectedNdcs(newValue)}
                        labelTooltip={NDC_FILTER_TOOLTIP}
                      />
                    </Grid>
                  )}

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Order Date'
                      name='orderDate'
                      id='WorkQueue-BulkLabelsFilterButton-orderDate-dateField'
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Label Date'
                      name='labelDate'
                      id='WorkQueue-BulkLabelsFilterButton-labelDate-dateField'
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <FacilityFilterField />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='State'
                      name='states'
                      id='WorkQueue-BulkLabelsFilterButton-state-input'
                      multiple
                      defaultValue={values.states}
                      options={usStateAbbreviation.map(({ abbreviation }) => abbreviation)}
                      sortOptions
                      value={selectedStates}
                      onChange={(event, newValue) => {
                        setSelectedStates(newValue);
                      }}
                    />
                  </Grid>

                  {EnableInventoryLookup ? (
                    <Grid item sx={{ py: 0.5 }}>
                      <AutoCompleteMultipleSelect
                        label='Drug Name'
                        name='drugProducts'
                        id='WorkQueue-BulkLabelsFilterButton-drugProduct-input'
                        multiple
                        // The catalog search already normalises spacing and punctuation on both
                        // sides; MUI's default filter does not, so left on it would hide results
                        // the server just matched (typing "Cabergoline0.5mg" drops the
                        // "Cabergoline 0.5MG" option).
                        serverSideSearch
                        defaultValue={selectedDrugProducts}
                        value={selectedDrugProducts}
                        loading={drugLoading}
                        options={drugProductOptionGroups.map((group) => group.label)}
                        renderOption={buildCatalogProductOptionRenderer(drugProductOptionGroups)}
                        sortOptions
                        onInputChange={(event, value) => {
                          setDrugNameInputValue(value ?? '');
                        }}
                        onChange={(event, newValue) => {
                          // Options are re-fetched on every keystroke, so a previously chosen
                          // label may no longer be in the current result set — carry the existing
                          // selections forward or removing one option would drop the others.
                          const availableGroups = [
                            ...drugProductOptionGroups,
                            ...selectedDrugProductObjects,
                          ];
                          const selectedObjects = newValue
                            .map((label) => availableGroups.find((group) => group.label === label))
                            .filter(Boolean);

                          setSelectedDrugProductObjects(selectedObjects);
                          setSelectedDrugProducts(newValue);
                        }}
                        noOptionsText={getDrugNameNoOptionsText()}
                      />
                    </Grid>
                  ) : (
                    <Grid item sx={{ py: 0.5 }}>
                      <AutoCompleteMultipleSelect
                        label='Drug Name'
                        name='medicationNames'
                        id='WorkQueue-BulkLabelsFilterButton-medicationName-input'
                        multiple
                        defaultValue={values.medicationNames}
                        loading={drugLoading}
                        options={drugNameOptions.map((drug) => drug.drugName)}
                        sortOptions
                        onInputChange={(event, value) => {
                          setDrugNameInputValue(value ?? '');
                        }}
                        noOptionsText={getDrugNameNoOptionsText()}
                      />
                    </Grid>
                  )}

                  {/*
                   * POM-5850: the Drug Profile filter is hidden once the inventory lookup is on —
                   * Drug Name above then filters by catalog product identity and covers it. It
                   * stays visible while the switch is off, where it is the live prod behaviour and
                   * the only drug filter that resolves against drug_profiles.
                   */}
                  {!EnableInventoryLookup && (
                    <Grid item sx={{ py: 0.5 }}>
                      <AutoCompleteMultipleSelect
                        label='Drug Profile'
                        name='drugProfiles'
                        id='WorkQueue-BulkLabelsFilterButton-drugProfile-input'
                        multiple
                        defaultValue={selectedDrugProfiles}
                        value={selectedDrugProfiles}
                        loading={drugProfileLoading}
                        options={drugProfileOptions.map((drug) => {
                          const displayText =
                            drug.strength && drug.form
                              ? `${drug.drugName} - ${drug.strength} ${drug.form}`
                              : drug.drugName;
                          return displayText;
                        })}
                        sortOptions
                        onInputChange={(event, value) => {
                          setDrugProfileInputValue(value ?? '');
                        }}
                        onChange={(event, newValue) => {
                          const allAvailableDrugs = [
                            ...drugProfileOptions,
                            ...selectedDrugProfileObjects,
                          ];
                          const uniqueAvailableDrugs = allAvailableDrugs.reduce((acc, drug) => {
                            if (!acc.find((d) => d.drugProfileId === drug.drugProfileId)) {
                              acc.push(drug);
                            }
                            return acc;
                          }, []);
                          const selectedObjects = newValue
                            .map((displayText) => {
                              const drug = uniqueAvailableDrugs.find((d) => {
                                const formatted =
                                  d.strength && d.form
                                    ? `${d.drugName} - ${d.strength} ${d.form}`
                                    : d.drugName;
                                return formatted === displayText;
                              });
                              return drug;
                            })
                            .filter(Boolean);
                          setSelectedDrugProfileObjects(selectedObjects);
                          setSelectedDrugProfiles(newValue);
                        }}
                        noOptionsText={getDrugProfileNoOptionsText()}
                      />
                    </Grid>
                  )}

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Not In Partner'
                      name='notInPartnerIds'
                      id='WorkQueue-BulkLabelsFilterButton-notInPartnerIds-input'
                      multiple
                      defaultValue={selectedNotInPartners}
                      options={partner}
                      sortOptions
                      value={selectedNotInPartners}
                      onChange={(event, newValue) => handleNotInPartnerChange(newValue)}
                      disabled={disableNotInPartnerFilter}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Partner'
                      name='partnerIds'
                      id='WorkQueue-BulkLabelsFilterButton-partnerIds-input'
                      multiple
                      defaultValue={selectedPartners}
                      options={partner}
                      sortOptions
                      value={selectedPartners}
                      onChange={(event, newValue) => handlePartnerChange(newValue)}
                      disabled={disablePartnerFilter}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      key={selectedPartnerIds.length}
                      label='Practice'
                      name='practiceIds'
                      id='WorkQueue-BulkLabelsFilterButton-practiceIds-input'
                      multiple
                      defaultValue={selectedPracticeNames}
                      disabled={!selectedPartnerIds.length}
                      loading={practiceLoading}
                      options={practices.map((practice) => casingFormat(practice.practiceName))}
                      sortOptions
                      value={selectedPractices}
                      onChange={(event, newValue) => {
                        const practiceUuids = newValue.map((practiceName) => {
                          const foundPractice = practices.find(
                            (practice) =>
                              practice.practiceName.toLowerCase() === practiceName.toLowerCase()
                          );

                          return foundPractice?.practiceId;
                        });

                        setSelectedPractices(newValue);
                        setSelectedPracticeIds(practiceUuids);
                      }}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Tags'
                      name='tags'
                      id='WorkQueue-BulkLabelsFilterButton-tags-input'
                      multiple
                      defaultValue={selectedTags?.map(formatTag)}
                      value={selectedTags?.map(formatTag)}
                      options={sortedTagOptions.map(formatTag)}
                      formatOption={formatTag}
                      sortOptions={false}
                      renderOption={renderTagOption}
                      onChange={(event, newValue) => {
                        const originalTags = newValue.map((formattedTag) =>
                          tagOptions.find((originalTag) => formatTag(originalTag) === formattedTag)
                        );
                        setSelectedTags(originalTags);
                      }}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TagMatchModeField
                      id='WorkQueue-BulkLabelsFilterButton-tagMatchMode-dropdown'
                      disabled={isEmpty(selectedTags)}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <AutoCompleteMultipleSelect
                      label='Tags Not In'
                      name='tagsNotIn'
                      id='WorkQueue-BulkLabelsFilterButton-tagsNotIn-input'
                      multiple
                      defaultValue={selectedTagsNotIn?.map(formatTag)}
                      value={selectedTagsNotIn?.map(formatTag)}
                      options={sortedTagOptions.map(formatTag)}
                      formatOption={formatTag}
                      sortOptions={false}
                      renderOption={renderTagOption}
                      onChange={(event, newValue) => {
                        const originalTags = newValue.map((formattedTag) =>
                          tagOptions.find((originalTag) => formatTag(originalTag) === formattedTag)
                        );
                        setSelectedTagsNotIn(originalTags);
                      }}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Ship By Date'
                      name='shipByDate'
                      id='WorkQueue-BulkLabelsFilterButton-shipByDate-dateField'
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Fill Type'
                      name={SortKey.FILL_TYPE}
                      id='WorkQueue-BulkLabelsFilterButton-fillType-dropdown'
                    >
                      <MenuItem value=''>All</MenuItem>
                      <MenuItem value={OrderFillType.FIRST_FILL}>First Fill</MenuItem>
                      <MenuItem value={OrderFillType.REFILL}>Refill</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Last Printed Date'
                      name='lastPrintedDate'
                      id='WorkQueue-BulkLabelsFilterButton-lastPrintedDate-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Printed'
                      name='isPrinted'
                      id='WorkQueue-BulkLabelsFilterButton-isPrinted-dropdown'
                    >
                      <MenuItem value>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Filled'
                      name='lineItemStatus'
                      id='WorkQueue-BulkLabelsFilterButton-status-dropdown'
                    >
                      <MenuItem value={lineItemEnum.PRODUCT_FULFILLMENT}>No</MenuItem>
                      <MenuItem value={lineItemEnum.PRODUCT_FULFILLED}>Yes</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Tagged For Review'
                      name='taggedForReview'
                      id='WorkQueue-BulkLabelsFilterButton-taggedForReview-dropdown'
                    >
                      <MenuItem value>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Reshipments'
                      name='isReshipment'
                      id='WorkQueue-BulkLabelsFilterButton-reshipment-dropdown'
                    >
                      <MenuItem value={undefined}>All</MenuItem>
                      <MenuItem value>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Batch Items'
                      name='batchStatus'
                      id='WorkQueue-BulkLabelsFilterButton-Batch-Shipment-dropdown'
                    >
                      <MenuItem value={undefined}>All</MenuItem>
                      <MenuItem value='IN_BATCH'>In Batch</MenuItem>
                      <MenuItem value='NOT_IN_BATCH'>Not In Batch</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Item Count'
                      name='itemCount'
                      id='WorkQueue-BulkLabelsFilterButton-itemCount-input'
                      type='number'
                      validations={[
                        validatePositiveInteger(),
                        validateMaxNumber(
                          `Must be ${GRAPHQL_INT_MAX.toLocaleString()} or less`,
                          GRAPHQL_INT_MAX
                        ),
                      ]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Qty'
                      name='quantity'
                      id='WorkQueue-BulkLabelsFilterButton-quantity-input'
                      type='number'
                      validations={[
                        validatePositiveInteger(),
                        validateMaxNumber(
                          `Must be ${GRAPHQL_INT_MAX.toLocaleString()} or less`,
                          GRAPHQL_INT_MAX
                        ),
                      ]}
                    />
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default BulkLabelsFilterButton;
